// Excel'den Stok Aktarma penceresi kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'inventory.import';

export const inventoryImportGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, "Excel'den stok nasıl aktarılır?"),
  intro: t(`${G}.intro`, 'Bu pencere, toplu ürün verisini bir Excel dosyasından güvenli şekilde içe aktarır. İşlem üç adımda ilerler: Veri Eşleştirme, Veri Önizleme ve Sonuç.'),
  navItems: [
    t(`${G}.nav.mapping`, 'Veri Eşleştirme'),
    t(`${G}.nav.preview`, 'Veri Önizleme'),
    t(`${G}.nav.result`, 'Sonuç'),
  ],
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık'),
    tabs: t(`${G}.regions.tabs`, 'Adımlar'),
    body: t(`${G}.regions.body`, 'Adım içeriği'),
    footer: t(`${G}.regions.footer`, 'Önizle / Aktar / Kapat'),
  },
  steps: [
    {
      region: 'tabs',
      title: t(`${G}.s1.title`, '1. Adımları takip edin'),
      text: t(`${G}.s1.text`, 'Üstteki numaralı adımlar nerede olduğunuzu gösterir: 1. adımda sütun eşleştirmesini ve ayarları yaparak veriyi sisteme uygun hale getirirsiniz, 2. adımda önizleme ile hataları kontrol edersiniz, 3. adımda aktarım sonucunu izlersiniz.'),
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Örnek dosyayı indirin ve ayarları yapın'),
      text: t(`${G}.s2.text`, '"Örnek Excel Dosyası İndir" ile doğru sütun düzenine sahip bir şablon alabilirsiniz. "İçe Aktarma Ayarları" bölümünde dosyanızın yapısına göre seçim yapın.'),
      tips: [
        t(`${G}.s2.tip1`, 'Veriler kaçıncı satırdan başlıyor?: başlık satırını atlamak için ilk ürün satırının numarasını yazın.'),
        t(`${G}.s2.tip2`, 'Başlık/boş satırları filtrele: açıkken, "Minimum dolu sütun sayısı"ndan az dolu hücresi olan satırlar atlanır.'),
        t(`${G}.s2.tip3`, 'Boş barkodlu ürünleri atla: açıkken barkodsuz ürünler aktarılmaz; kapalıyken bunlara otomatik barkod atanır.'),
        t(`${G}.s2.tip4`, 'Stok eksiye düşebilir: aktarılan ürünlerin stoğunun eksiye düşmesine izin verir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Sütunları eşleştirin ve dosyayı seçin'),
      text: t(`${G}.s3.text`, '"Excel Sütun Eşleştirmeleri" bölümünde her alanın Exceldeki sütun harfini (A, B, C…) yazın. Alış/Satış para birimi ve ÖTV durumu sütun yerine listeden seçilir. Sonra "Excel Dosyasını Yükle" alanına tıklayıp .xlsx / .xls dosyanızı seçin; dosyada birden fazla sayfa varsa "Çalışma Sayfası"nı seçin.'),
      tips: [
        t(`${G}.s3.tip1`, '* ile işaretli alanlar zorunludur (Ürün Adı ve Satış Fiyatı). Zorunlu alanlar eşleşmeden devam edilemez.'),
        t(`${G}.s3.tip2`, 'Tüm ürünler otomatik olarak merkez depoya eklenir; daha sonra diğer depolara transfer edebilirsiniz.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Önizlemede hataları kontrol edin'),
      text: t(`${G}.s4.text`, '"Önizle"ye bastığınızda hızlı doğrulama çalışır: toplam, yüklenebilir ve hatalı ürün sayıları gösterilir. Altta ilk 10 satırın önizlemesi yer alır; eşleşmelerin doğru olduğunu buradan kontrol edin.'),
      tips: [
        t(`${G}.s4.tip1`, 'Veritabanı çakışmaları: stok kodu veya barkodu sistemde zaten olan ürünler.'),
        t(`${G}.s4.tip2`, 'Excel içi duplikasyonlar: dosyada birden fazla kez geçen ürünler.'),
        t(`${G}.s4.tip3`, 'Eksik zorunlu alanlar: zorunlu hücresi boş satırlar. Hatalı satırları "Sonuç Excel\'ini İndir" ile listeleyebilirsiniz.'),
        t(`${G}.s4.tip4`, 'Önizleme boşsa satırlar başlık/boş olarak filtrelenmiş olabilir; filtreyi kapatın veya minimum dolu sütun sayısını azaltın. Düzeltme için "Geri" ile 1. adıma dönün.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s5.title`, '5. Verileri aktarın'),
      text: t(`${G}.s5.text`, '"Verileri Aktar"a basın. Aktarım sırasında kategoriler, markalar, resimler ve stok ürünleri için ilerleme çubukları görünür; yeni kategori ve markalar otomatik oluşturulur.'),
      tips: [
        t(`${G}.s5.tip1`, 'Aktarım sürerken pencereyi kapatmak isterseniz onay sorulur. İptal ederseniz şu ana kadar aktarılan ürünler veritabanında kalır; bu geri alınamaz.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s6.title`, '6. Aktarım sonucunu doğrulayın'),
      text: t(`${G}.s6.text`, '3. adımda "İçe Aktarma Özeti" kaydedilen kategori, marka, resim ve ürün sayılarını gösterir. Uyarıları kontrol ederek kayıt kalitesini doğrulayın, sonra "Kapat"a basın.'),
      tips: [
        t(`${G}.s6.tip1`, 'Barkodu sistemde zaten kullanılan ürünler aktarılmaz; "Barkod Çakışmaları" listesinde çakışan ürünle birlikte gösterilir.'),
        t(`${G}.s6.tip2`, 'Başarısız ürün, resim veya barkod çakışması varsa "Hata Logunu İndir (Excel)" ile ayrıntıları indirebilirsiniz.'),
      ],
    },
  ],
});
